import { useState, useRef, useCallback } from 'react';

interface UseShiftSelectOptions<T extends { id: string }> {
  items: T[];
}

interface UseShiftSelectReturn {
  selectedItems: string[];
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>;
  handleSelect: (id: string, index: number, shiftKey: boolean) => void;
  handleSelectAll: (checked: boolean) => void;
  clearSelection: () => void;
}

/**
 * Hook for managing multi-item selection with Shift+Click support
 * 
 * Enables selecting a range of items by:
 * 1. Clicking an item to select it (stores as lastSelectedIndex)
 * 2. Holding Shift and clicking another item
 * 3. All items between the two clicks are selected
 */
export function useShiftSelect<T extends { id: string }>({
  items,
}: UseShiftSelectOptions<T>): UseShiftSelectReturn {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const lastSelectedIndex = useRef<number | null>(null);

  const handleSelect = useCallback(
    (id: string, index: number, shiftKey: boolean) => {
      if (shiftKey && lastSelectedIndex.current !== null) {
        // Shift+Click: select range
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        
        const rangeIds = items
          .slice(start, end + 1)
          .map(item => item.id);
        
        setSelectedItems(prev => {
          // Add all items in range to selection (avoid duplicates)
          const newSelection = new Set([...prev, ...rangeIds]);
          return Array.from(newSelection);
        });
      } else {
        // Normal click: toggle individual item
        setSelectedItems(prev =>
          prev.includes(id)
            ? prev.filter(itemId => itemId !== id)
            : [...prev, id]
        );
        lastSelectedIndex.current = index;
      }
    },
    [items]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedItems(items.map(item => item.id));
      } else {
        setSelectedItems([]);
        lastSelectedIndex.current = null;
      }
    },
    [items]
  );

  const clearSelection = useCallback(() => {
    setSelectedItems([]);
    lastSelectedIndex.current = null;
  }, []);

  return {
    selectedItems,
    setSelectedItems,
    handleSelect,
    handleSelectAll,
    clearSelection,
  };
}
